import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, FileSignature, Plus, Trash2, Euro, CheckCircle, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';
import DatePickerField from '@/components/DatePickerField';

export default function ContrattoPreventivo() {
  const { prevId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prevData, setPrevData] = useState(null);
  const [form, setForm] = useState({
    data_stipula: new Date().toISOString().slice(0, 10),
    luogo_stipula: 'Celano',
    note_aggiuntive: '',
    garanti: [{ nome: '', cognome: '', data_nascita: '', luogo_nascita: '', codice_fiscale: '', indirizzo_residenza: '', parentela: '' }],
  });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/preventivi/${prevId}/dati-contratto`).then(r => {
      setPrevData(r.data);
      // Pre-fill referente as first garante
      if (r.data.referente_nome) {
        const parts = r.data.referente_nome.split(' ');
        setForm(f => ({
          ...f,
          garanti: [{ ...f.garanti[0], nome: parts.slice(1).join(' ') || '', cognome: parts[0] || '', parentela: 'Referente' }]
        }));
      }
    }).catch(() => toast.error('Errore caricamento dati preventivo')).finally(() => setLoading(false));
  }, [prevId]);

  const addGarante = () => {
    setForm(f => ({ ...f, garanti: [...f.garanti, { nome: '', cognome: '', data_nascita: '', luogo_nascita: '', codice_fiscale: '', indirizzo_residenza: '', parentela: '' }] }));
  };

  const removeGarante = (idx) => {
    setForm(f => ({ ...f, garanti: f.garanti.filter((_, i) => i !== idx) }));
  };

  const updateGarante = (idx, field, value) => {
    setForm(f => {
      const g = [...f.garanti];
      g[idx] = { ...g[idx], [field]: value };
      return { ...f, garanti: g };
    });
  };

  const handleDownload = async () => {
    if (!prevData) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('rsa_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contratto/genera-da-preventivo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nome: prevData.nome,
          cognome: prevData.cognome,
          data_nascita: prevData.data_nascita || '',
          codice_fiscale: prevData.codice_fiscale || '',
          garanti: form.garanti.filter(g => g.cognome),
          retta_mensile: prevData.retta_mensile || 1600,
          data_stipula: form.data_stipula,
          luogo_stipula: form.luogo_stipula,
          note_aggiuntive: form.note_aggiuntive,
        })
      });
      if (!response.ok) throw new Error('Errore generazione contratto');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('content-disposition');
      const filename = disposition?.match(/filename="?(.+?)"?$/)?.[1] || `Contratto_${prevData.cognome}_${prevData.nome}.docx`;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Contratto scaricato');
    } catch {
      toast.error('Errore nella generazione del contratto');
    }
    setDownloading(false);
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Caricamento...</div>;
  if (!prevData) return <div className="text-center py-12 text-red-500">Preventivo non trovato</div>;

  const p = prevData;
  const serviziAttivi = p.servizi_accessori || [];

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="contratto-preventivo-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/preventivi')} className="h-8"><ArrowLeft className="w-4 h-4 mr-1" />Torna</Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 font-[Cinzel]">Genera Contratto</h1>
          <p className="text-sm text-slate-500">Da Preventivo n. {p.numero_preventivo} - {p.cognome} {p.nome}</p>
        </div>
      </div>

      {/* Riepilogo preventivo */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileSignature className="w-4 h-4 text-brand" />Riepilogo Preventivo</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-xs text-slate-400 block">Candidato</span><strong>{p.cognome} {p.nome}</strong></div>
            <div><span className="text-xs text-slate-400 block">Codice Fiscale</span><span className="font-mono text-xs">{p.codice_fiscale || '-'}</span></div>
            <div><span className="text-xs text-slate-400 block">Soggiorno</span>{p.tipologia_soggiorno}</div>
            <div><span className="text-xs text-slate-400 block">Camera</span>{p.tipologia_camera}</div>
          </div>
          <Separator className="my-3" />
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><span className="text-xs text-slate-400 block">Retta Base/mese</span><strong className="text-emerald-700"><Euro className="w-3 h-3 inline" /> {p.retta_mensile_base?.toFixed(2)}</strong></div>
            <div><span className="text-xs text-slate-400 block">Servizi/mese</span><strong><Euro className="w-3 h-3 inline" /> {p.servizi_totale_mese?.toFixed(2)}</strong></div>
            <div><span className="text-xs text-slate-400 block">Totale Mensile</span><strong className="text-lg text-emerald-700"><Euro className="w-3 h-3 inline" /> {p.retta_mensile?.toFixed(2)}</strong></div>
          </div>
          {serviziAttivi.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {serviziAttivi.map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">{s.nome} (EUR {parseFloat(s.prezzo).toFixed(0)}/m)</Badge>)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dati contratto */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Dati Contratto</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Data Stipula</Label><DatePickerField value={form.data_stipula} onChange={v => setForm(f => ({ ...f, data_stipula: v }))} /></div>
            <div><Label className="text-xs">Luogo Stipula</Label><Input value={form.luogo_stipula} onChange={e => setForm(f => ({ ...f, luogo_stipula: e.target.value }))} /></div>
          </div>
          <div><Label className="text-xs">Note Aggiuntive</Label><DictableTextarea value={form.note_aggiuntive} onChange={e => setForm(f => ({ ...f, note_aggiuntive: e.target.value }))} rows={2} /></div>
        </CardContent>
      </Card>

      {/* Garanti */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Garanti / Firmatari</CardTitle>
            <Button size="sm" variant="outline" onClick={addGarante} className="h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" />Aggiungi Garante</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.garanti.map((g, idx) => (
            <div key={idx} className="border rounded-lg p-3 bg-slate-50 space-y-3">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="text-[10px]">Garante {idx + 1}</Badge>
                {form.garanti.length > 1 && <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeGarante(idx)}><Trash2 className="w-3 h-3 text-slate-400" /></Button>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Nome</Label><Input value={g.nome} onChange={e => updateGarante(idx, 'nome', e.target.value)} data-testid={`garante-nome-${idx}`} /></div>
                <div><Label className="text-xs">Cognome</Label><Input value={g.cognome} onChange={e => updateGarante(idx, 'cognome', e.target.value)} data-testid={`garante-cognome-${idx}`} /></div>
                <div><Label className="text-xs">Parentela</Label><Input value={g.parentela} onChange={e => updateGarante(idx, 'parentela', e.target.value)} placeholder="Figlio, Coniuge..." /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Data Nascita</Label><DatePickerField value={g.data_nascita} onChange={v => updateGarante(idx, 'data_nascita', v)} /></div>
                <div><Label className="text-xs">Luogo Nascita</Label><Input value={g.luogo_nascita} onChange={e => updateGarante(idx, 'luogo_nascita', e.target.value)} /></div>
                <div><Label className="text-xs">Codice Fiscale</Label><Input value={g.codice_fiscale} onChange={e => updateGarante(idx, 'codice_fiscale', e.target.value)} /></div>
              </div>
              <div><Label className="text-xs">Indirizzo Residenza</Label><Input value={g.indirizzo_residenza} onChange={e => updateGarante(idx, 'indirizzo_residenza', e.target.value)} /></div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Azioni */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/preventivi')}>Annulla</Button>
        <Button onClick={handleDownload} disabled={downloading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="print-contratto">
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {downloading ? 'Generazione...' : 'Scarica Contratto DOCX'}
        </Button>
      </div>
    </div>
  );
}
